import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useEnabledQuery } from '../../../../../ui/hooks/useEnabledQuery';
import { getMutationSchema } from '../../../../../ui/util/getMutationSchema';
import { ResourceType } from '../../../../enumerations/ResourceType';
import { VCycleElementType, VCycleElementTypeSchema } from '../../../../enumerations/VCycleElementType';
import { VCycleSide, VCycleSideSchema } from '../../../../enumerations/VCycleSide';
import { StructuredVCycleObjectOutlineSchema } from '../../../../outlines/vcycleElementOutline';
import { get } from '../../../../resource-base/APIVersion1';
import { parseRawData, useOnUnauthorized } from '../../../../resource-base/adapters/AdapterUtils';
import { useIndirectlyVersionedResourceAdapter } from '../../../../resource-base/adapters/useIndirectlyVersionedResourceAdapter';
import { useMultiResourceSingleParentedResourceAdapterActions } from '../../../../resource-base/adapters/useMultiResourceSingleParentedResourceAdapterActions';
import {
    defaultIndirectlyVersionedArtifactResource,
    IndirectlyVersionedArtifactResourceSchema,
} from '../../../../resource-base/artifactResourceTypes';
import { ParamConstants } from '../../../../resource-base/types';

export enum VCycleElementContainerPropertyValidation {
    TYPE = 'TYPE',
    SIDE = 'SIDE',
    INDEX = 'INDEX',
    ROW_SPAN = 'ROW_SPAN',
}

const VCycleElementContainerResourceSchema = IndirectlyVersionedArtifactResourceSchema.extend({
    type: VCycleElementTypeSchema,
    side: VCycleSideSchema,
    index: z.number(),
    rowSpan: z.number(),
    resourceType: z.literal(ResourceType.V_CYCLE_ELEMENT_CONTAINER).default(ResourceType.V_CYCLE_ELEMENT_CONTAINER),
});

export const VCycleElementContainerMutationResourceSchema = getMutationSchema(VCycleElementContainerResourceSchema);

export type VCycleElementContainerResource = z.infer<typeof VCycleElementContainerResourceSchema>;

export const defaultVCycleElementContainerResource: VCycleElementContainerResource = {
    ...defaultIndirectlyVersionedArtifactResource,
    resourceType: ResourceType.V_CYCLE_ELEMENT_CONTAINER,
    side: VCycleSide.LEFT,
    index: 1,
    rowSpan: 1,
    type: VCycleElementType.PROCESS_AREA,
};

export function useVCycleElementContainerActions() {
    const onUnauthorized = useOnUnauthorized();
    const getEnabled = useEnabledQuery();
    const actions = {
        ...useMultiResourceSingleParentedResourceAdapterActions(
            'project/v-cycle-element-container',
            ResourceType.V_CYCLE_ELEMENT_CONTAINER,
            VCycleElementContainerResourceSchema
        ),
        ...useIndirectlyVersionedResourceAdapter(
            'project/v-cycle-element-container',
            ResourceType.V_CYCLE_ELEMENT_CONTAINER,
            VCycleElementContainerResourceSchema
        ),
    };

    const useGetStructuredVCycleElementContainers = (parentId: string | undefined | null) => {
        return useQuery({
            queryKey: [parentId, ResourceType.V_CYCLE_ELEMENT_CONTAINER, 'STRUCTURED'],
            queryFn: () =>
                get(
                    `project/v-cycle-element-container/structured?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}`
                ).then((result) => {
                    if (result.status === 200) {
                        return result
                            .json()
                            .then((outline) => parseRawData(outline, StructuredVCycleObjectOutlineSchema));
                    } else if (result.status === 401) {
                        return onUnauthorized(result, null).then((_) => {
                            throw Error('Unauthorized!');
                        });
                    }
                }),
            enabled: getEnabled(parentId),
        });
    };

    return {
        ...actions,
        useGetStructuredVCycleElementContainers,
    };
}
